import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { fmtDate } from "../lib/format";
import { Plus, Pencil, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PERMS = [
  { k: "dashboard", l: "Dashboard & Daily Profit" },
  { k: "chimes", l: "Chimes" },
  { k: "cashapps", l: "CashApp" },
  { k: "paypals", l: "PayPal" },
  { k: "venmos", l: "Venmo" },
  { k: "links", l: "Links" },
  { k: "withdrawals", l: "Withdrawals" },
  { k: "usdt_sent", l: "USDT Sent" },
  { k: "expenses", l: "Expenses" },
  { k: "salaries", l: "EM Salary Sheets" },
  { k: "team_analysis", l: "Team Analysis" },
  { k: "net_profits", l: "Net Profits" },
  { k: "rates", l: "Rates" },
  { k: "admins", l: "Admins & Roles" },
];

export default function Admins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // admin modal
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminEditing, setAdminEditing] = useState(null);
  const [adminForm, setAdminForm] = useState({
    email: "",
    password: "",
    name: "",
    role_id: "",
  });

  // role modal
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleEditing, setRoleEditing] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    permissions: [],
    cf_team: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        api.get("/admins"),
        api.get("/roles"),
      ]);
      setAdmins(a.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Admins
  const openAdminCreate = () => {
    setAdminEditing(null);
    setAdminForm({ email: "", password: "", name: "", role_id: "" });
    setAdminOpen(true);
  };
  const openAdminEdit = (a) => {
    setAdminEditing(a);
    setAdminForm({
      email: a.email,
      password: "",
      name: a.name,
      role_id: a.role_id || "",
    });
    setAdminOpen(true);
  };
  const saveAdmin = async () => {
    try {
      if (adminEditing) {
        const payload = {
          name: adminForm.name,
          role_id: adminForm.role_id || null,
        };
        if (adminForm.password) payload.password = adminForm.password;
        await api.put(`/admins/${adminEditing.id}`, payload);
        toast.success("Admin updated");
      } else {
        await api.post("/admins", {
          email: adminForm.email,
          password: adminForm.password,
          name: adminForm.name,
          role_id: adminForm.role_id || null,
        });
        toast.success("Admin added");
      }
      setAdminOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };
  const deleteAdmin = async (a) => {
    if (!window.confirm(`Delete admin ${a.email}?`)) return;
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  // Roles
  const openRoleCreate = () => {
    setRoleEditing(null);
    setRoleForm({ name: "", description: "", permissions: [], cf_team: false });
    setRoleOpen(true);
  };
  const openRoleEdit = (r) => {
    setRoleEditing(r);
    setRoleForm({
      name: r.name,
      description: r.description || "",
      permissions: r.permissions || [],
      cf_team: !!r.cf_team,
    });
    setRoleOpen(true);
  };
  const saveRole = async () => {
    try {
      if (roleEditing) {
        await api.put(`/roles/${roleEditing.id}`, roleForm);
        toast.success("Role updated");
      } else {
        await api.post("/roles", roleForm);
        toast.success("Role created");
      }
      setRoleOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };
  const deleteRole = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const togglePerm = (k) => {
    setRoleForm((f) => ({
      ...f,
      permissions: f.permissions.includes(k)
        ? f.permissions.filter((x) => x !== k)
        : [...f.permissions, k],
    }));
  };

  return (
    <Layout
      title="Admins & Roles"
      subtitle="Master admin controls — invite admins and define custom role permissions"
      testId="admins-page"
      actions={
        user?.is_master && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={openRoleCreate}
              className="h-9"
              data-testid="add-role-btn"
            >
              <Shield size={14} className="mr-1.5" /> New role
            </Button>
            <Button
              onClick={openAdminCreate}
              className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="add-admin-btn"
            >
              <Plus size={14} className="mr-1.5" /> New admin
            </Button>
          </div>
        )
      }
    >
      {/* Roles section */}
      <section className="mb-8">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-display text-[17px] font-bold tracking-tight">
              Custom Roles
            </h2>
            <p className="text-[12px] text-zinc-500">
              Each role is a set of module permissions.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.length === 0 && !loading && (
            <div className="stat-card p-5 text-[13px] text-zinc-500">
              No custom roles yet. Master admin has all permissions by default.
            </div>
          )}
          {roles.map((r) => (
            <div key={r.id} className="stat-card p-5" data-testid={`role-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold tracking-tight">
                    {r.name}
                  </div>
                  <div className="text-[12px] text-zinc-500">
                    {r.description || "—"}
                  </div>
                </div>
                {user?.is_master && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => openRoleEdit(r)}
                      className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                      data-testid={`edit-role-${r.id}`}
                    >
                      <Pencil size={14} strokeWidth={1.6} />
                    </button>
                    <button
                      onClick={() => deleteRole(r)}
                      className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                      data-testid={`delete-role-${r.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {PERMS.map((p) =>
                  r.permissions?.includes(p.k) ? (
                    <Badge
                      key={p.k}
                      variant="outline"
                      className="text-[11px] bg-zinc-900 text-white border-zinc-900"
                    >
                      {p.l}
                    </Badge>
                  ) : (
                    <Badge
                      key={p.k}
                      variant="outline"
                      className="text-[11px] text-zinc-400"
                    >
                      {p.l}
                    </Badge>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Admins section */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-display text-[17px] font-bold tracking-tight">
              Admins
            </h2>
            <p className="text-[12px] text-zinc-500">
              Users with access to the system.
            </p>
          </div>
        </div>
        <div className="stat-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                  <th className="py-3 px-5 font-medium">Name</th>
                  <th className="py-3 px-5 font-medium">Email</th>
                  <th className="py-3 px-5 font-medium">Role</th>
                  <th className="py-3 px-5 font-medium">Created</th>
                  <th className="py-3 px-5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-zinc-500">
                      Loading…
                    </td>
                  </tr>
                ) : (
                  admins.map((a) => (
                    <tr key={a.id} className="row-hover" data-testid={`admin-row-${a.id}`}>
                      <td className="py-3 px-5 font-medium flex items-center gap-2">
                        {a.name}
                        {a.is_master && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-amber-50 border-amber-200 text-amber-700"
                          >
                            Master
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-5 text-zinc-600">{a.email}</td>
                      <td className="py-3 px-5">
                        {a.is_master ? (
                          <span className="text-zinc-500 text-[12.5px]">
                            All permissions
                          </span>
                        ) : a.role ? (
                          a.role.name
                        ) : (
                          <span className="text-zinc-400 text-[12.5px]">No role</span>
                        )}
                      </td>
                      <td className="py-3 px-5 font-mono text-[12px] text-zinc-500">
                        {fmtDate(a.created_at)}
                      </td>
                      <td className="py-3 px-5">
                        <div className="flex justify-end gap-1">
                          {user?.is_master && (
                            <>
                              <button
                                onClick={() => openAdminEdit(a)}
                                disabled={a.is_master && a.id !== user.id}
                                className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600 disabled:opacity-30"
                                data-testid={`edit-admin-${a.id}`}
                              >
                                <Pencil size={14} strokeWidth={1.6} />
                              </button>
                              <button
                                onClick={() => deleteAdmin(a)}
                                disabled={a.is_master}
                                className="p-1.5 rounded hover:bg-rose-50 text-rose-600 disabled:opacity-30"
                                data-testid={`delete-admin-${a.id}`}
                              >
                                <Trash2 size={14} strokeWidth={1.6} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Admin dialog */}
      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {adminEditing ? "Edit admin" : "Invite admin"}
            </DialogTitle>
            <DialogDescription>
              {adminEditing
                ? "Leave password blank to keep it unchanged."
                : "Admin can sign in immediately with this password."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Full name *</Label>
              <Input
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                disabled={!!adminEditing}
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{adminEditing ? "New password" : "Password *"}</Label>
              <Input
                type="password"
                value={adminForm.password}
                onChange={(e) =>
                  setAdminForm({ ...adminForm, password: e.target.value })
                }
                data-testid="admin-password-input"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Role</Label>
              <Select
                value={adminForm.role_id || "none"}
                onValueChange={(v) =>
                  setAdminForm({ ...adminForm, role_id: v === "none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="admin-role-select">
                  <SelectValue placeholder="No role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No role (view-only)</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveAdmin}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-admin-btn"
            >
              {adminEditing ? "Save" : "Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role dialog */}
      <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
        <DialogContent data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {roleEditing ? "Edit role" : "New role"}
            </DialogTitle>
            <DialogDescription>
              Define which modules this role can access.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Role name *</Label>
              <Input
                value={roleForm.name}
                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                placeholder="e.g. Wallet Operator"
                data-testid="role-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={roleForm.description}
                onChange={(e) =>
                  setRoleForm({ ...roleForm, description: e.target.value })
                }
                placeholder="Optional"
                data-testid="role-description-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="mb-2 block">Permissions</Label>
              <div className="grid grid-cols-2 gap-2">
                {PERMS.map((p) => (
                  <label
                    key={p.k}
                    className="flex items-center gap-2 border hairline rounded-md px-3 py-2 cursor-pointer hover:bg-zinc-50"
                  >
                    <Checkbox
                      checked={roleForm.permissions.includes(p.k)}
                      onCheckedChange={() => togglePerm(p.k)}
                      data-testid={`perm-${p.k}`}
                    />
                    <span className="text-[13px]">{p.l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <label
                className="flex items-start gap-2 border-2 border-amber-200 rounded-md px-3 py-2.5 cursor-pointer hover:bg-amber-50/50 bg-amber-50/30"
                data-testid="role-cf-team-toggle"
              >
                <Checkbox
                  checked={!!roleForm.cf_team}
                  onCheckedChange={(v) =>
                    setRoleForm({ ...roleForm, cf_team: !!v })
                  }
                  data-testid="role-cf-team-checkbox"
                />
                <div>
                  <div className="text-[13px] font-medium text-amber-900">
                    CF Team mode
                  </div>
                  <div className="text-[11.5px] text-amber-800/80 mt-0.5">
                    Restricts the user to <b>today + yesterday</b> daily profit
                    only. Hides monthly, all-time, and window totals on Daily
                    Profit. Pair with the <i>Dashboard &amp; Daily Profit</i>{" "}
                    permission.
                  </div>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveRole}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-role-btn"
            >
              {roleEditing ? "Save" : "Create role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
